import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Badge,
  Button,
  Loading,
  Meter,
  PageHeader,
  Panel,
  PanelBody,
  PanelHeader,
  Stat,
  Table,
  TableShell,
  TBody,
  Td,
  Th,
  THead,
  Tr,
} from '../ui/index.js';
import { int, pct, score, stamp } from '../lib/format.js';
import type { Tone } from '../ui/index.js';

interface GoldenMetrics {
  readonly total: number;
  readonly perBand: Record<string, { total: number; correct: number }>;
  readonly precisionAuto: number | null;
  readonly recallAuto: number | null;
  readonly gate: 'pass' | 'fail' | 'insufficient';
  readonly failures?: readonly {
    sku: string;
    expected: string;
    predicted: string;
    score: number;
  }[];
  readonly error?: string;
}

interface QualityPayload {
  readonly queue: Record<string, number>;
  readonly candidates: Record<string, number>;
  readonly openConflicts: Record<string, number>;
  readonly sources: readonly {
    readonly key: string;
    readonly label: string;
    readonly trust: number;
    readonly enabled: boolean;
    readonly offers: number;
    readonly lastCrawl: { status: string; finishedAt: string; offersCreated: number } | null;
  }[];
  readonly golden: GoldenMetrics | null;
}

const GOLDEN_TARGET = 300;

function gateTone(gate: GoldenMetrics['gate']): 'good' | 'alarm' | 'signal' {
  if (gate === 'pass') return 'good';
  if (gate === 'fail') return 'alarm';
  return 'signal';
}

async function fetchJson<T>(path: string, init?: RequestInit): Promise<T> {
  const hasBody = init?.body !== undefined && init.body !== null;
  const headers = new Headers(init?.headers);
  if (!headers.has('accept')) {
    headers.set('accept', 'application/json');
  }
  if (hasBody && !headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }
  const response = await fetch(path, {
    credentials: 'include',
    ...init,
    headers,
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `HTTP ${response.status}`);
  }
  return (await response.json()) as T;
}

function trigger(path: string, body: unknown): Promise<{ status: string; jobId: string | null }> {
  return fetchJson(path, { method: 'POST', body: JSON.stringify(body ?? {}) });
}

/** S07 — Matching quality (docs/09 §1): bands, evidence pool, golden gate, crawl triggers. */
export function MatchingQualityPage(): React.ReactElement {
  const queryClient = useQueryClient();
  const [message, setMessage] = useState<string | null>(null);
  const [messageTone, setMessageTone] = useState<Tone>('signal');
  const qualityQuery = useQuery({
    queryKey: ['match-quality'],
    queryFn: () => fetchJson<QualityPayload>('/api/v1/match/quality'),
  });

  const runMatch = useMutation({
    mutationFn: () => trigger('/api/v1/match/run', {}),
    onSuccess: () => {
      setMessageTone('signal');
      setMessage(
        'Matching lancé — la file se vide via le worker (actualisez dans une minute, ou relancez la lecture).',
      );
      void queryClient.invalidateQueries({ queryKey: ['match-quality'] });
    },
    onError: (error) => {
      setMessageTone('alarm');
      setMessage(`Échec : ${String(error.message)}`);
    },
  });

  const runCrawl = useMutation({
    mutationFn: (sourceKey: string) => trigger('/api/v1/crawl/run', { sourceKey }),
    onSuccess: (result, sourceKey) => {
      setMessageTone('scan');
      setMessage(
        `Crawl ${sourceKey} lancé (${result.jobId ?? 'en file'}) — les offres de comparaison arrivent progressivement.`,
      );
    },
    onError: (error) => {
      setMessageTone('alarm');
      setMessage(`Échec : ${String(error.message)}`);
    },
  });

  // P4-lite: purchase prices from supplier files/portals (GTIN + reference paths)
  const runPricing = useMutation({
    mutationFn: () => trigger('/api/v1/pricing/run', {}),
    onSuccess: (result) => {
      setMessageTone('scan');
      setMessage(
        `Calcul des prix d'achat lancé (${result.jobId ?? 'en file'}) — Prix achat HT et TVA remplis depuis les fichiers fournisseurs et portails uniquement (jamais les sites concurrents).`,
      );
    },
    onError: (error) => {
      setMessageTone('alarm');
      setMessage(`Échec : ${String(error.message)}`);
    },
  });

  const quality = qualityQuery.data;
  const openConflicts =
    quality === undefined ? 0 : Object.values(quality.openConflicts).reduce((a, b) => a + b, 0);

  return (
    <div>
      <PageHeader
        index="S07"
        title="Qualité du matching"
        purpose="Contrôle du moteur de rapprochement : remplissage des bandes de confiance, volume de preuves par source de comparaison, et porte de qualité du jeu de référence vérifié à la main."
        meta={[
          {
            label: 'Bande review',
            value: quality === undefined ? '—' : int(quality.queue['review'] ?? 0),
          },
          {
            label: 'Bande auto',
            value: quality === undefined ? '—' : int(quality.queue['auto'] ?? 0),
          },
          {
            label: 'Sources actives',
            value:
              quality === undefined ? '—' : int(quality.sources.filter((s) => s.enabled).length),
          },
        ]}
        actions={
          <>
            <Button
              type="button"
              variant="outline"
              size="md"
              disabled={runPricing.isPending}
              onClick={() => runPricing.mutate()}
            >
              {runPricing.isPending ? 'Lancement…' : 'Recalculer les prix d’achat'}
            </Button>
            <Button
              type="button"
              variant="primary"
              size="md"
              disabled={runMatch.isPending}
              onClick={() => runMatch.mutate()}
            >
              {runMatch.isPending ? 'Lancement…' : 'Lancer le matching'}
            </Button>
          </>
        }
      />

      {message !== null && (
        <Alert
          tone={messageTone === 'alarm' ? 'alarm' : 'info'}
          title="Journal des déclenchements"
          className="mb-4"
        >
          {message}
        </Alert>
      )}

      {qualityQuery.isError && (
        <Alert tone="alarm" title="Qualité indisponible" className="mb-4">
          {String(qualityQuery.error.message)}
        </Alert>
      )}

      {qualityQuery.isPending && <Loading label="Lecture des compteurs de matching…" />}

      {quality !== undefined && (
        <>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
            <Stat
              label="File review"
              value={int(quality.queue['review'] ?? 0)}
              hint="à valider à la main (S02)"
              tone="signal"
              index="Q1"
            />
            <Stat
              label="File auto"
              value={int(quality.queue['auto'] ?? 0)}
              hint="candidat unique et net"
              tone="good"
              index="Q2"
            />
            <Stat
              label="Candidats auto"
              value={int(quality.candidates['auto'] ?? 0)}
              hint="scores au-dessus du seuil haut"
              tone="good"
              index="Q3"
            />
            <Stat
              label="Candidats review"
              value={int(quality.candidates['review'] ?? 0)}
              hint="scores intermédiaires"
              tone="scan"
              index="Q4"
            />
            <Stat
              label="Conflits ouverts"
              value={int(openConflicts)}
              hint="toutes catégories (S03)"
              tone={openConflicts > 0 ? 'alarm' : 'good'}
              index="Q5"
            />
          </div>

          <div className="mt-4 grid gap-4 xl:grid-cols-[minmax(0,1fr)_380px]">
            <Panel>
              <PanelHeader
                index="§"
                title="Pool de preuves par source"
                hint="confiance, volume d'offres, dernier crawl"
              />
              <TableShell plain>
                <Table>
                  <THead>
                    <tr>
                      <Th>Source</Th>
                      <Th align="right">Confiance</Th>
                      <Th>Poids</Th>
                      <Th align="right">Offres</Th>
                      <Th>Dernier crawl</Th>
                      <Th />
                    </tr>
                  </THead>
                  <TBody>
                    {quality.sources.map((source) => (
                      <Tr key={source.key}>
                        <Td>
                          <span className="flex items-center gap-2">
                            <span className="text-ink">{source.label}</span>
                            {!source.enabled && <Badge tone="muted">inactif</Badge>}
                          </span>
                          <span className="font-mono text-[10px] text-ink-faint">{source.key}</span>
                        </Td>
                        <Td align="right" className="tabular font-mono text-ink">
                          {source.trust}
                        </Td>
                        <Td className="w-24">
                          <Meter
                            value={source.trust / 100}
                            tone={source.trust >= 80 ? 'good' : 'scan'}
                          />
                        </Td>
                        <Td align="right" className="tabular font-mono text-ink">
                          {int(source.offers)}
                        </Td>
                        <Td className="font-mono text-[10px] text-ink-soft">
                          {source.lastCrawl === null
                            ? 'jamais'
                            : `${source.lastCrawl.status} · ${stamp(source.lastCrawl.finishedAt)} (+${int(source.lastCrawl.offersCreated)})`}
                        </Td>
                        <Td align="right">
                          <Button
                            type="button"
                            variant="outline"
                            size="xs"
                            disabled={runCrawl.isPending || !source.enabled}
                            onClick={() => runCrawl.mutate(source.key)}
                          >
                            {runCrawl.isPending && runCrawl.variables === source.key
                              ? 'Crawl…'
                              : 'Crawler'}
                          </Button>
                        </Td>
                      </Tr>
                    ))}
                  </TBody>
                </Table>
              </TableShell>
            </Panel>

            <Panel tone={quality.golden === null ? 'neutral' : gateTone(quality.golden.gate)}>
              <PanelHeader
                index="GD"
                title="Golden dataset"
                hint="porte de qualité (docs/10 §3)"
                tone={quality.golden === null ? 'neutral' : gateTone(quality.golden.gate)}
              />
              <PanelBody>
                {quality.golden === null ? (
                  <p className="text-sm text-ink-soft">
                    Fixture absente de ce déploiement — le jeu de référence se charge avec les
                    données de test.
                  </p>
                ) : (
                  <>
                    <div className="flex items-center justify-between gap-3">
                      <Badge tone={gateTone(quality.golden.gate)}>
                        porte{' '}
                        {quality.golden.gate === 'pass'
                          ? 'franchie'
                          : quality.golden.gate === 'fail'
                            ? 'échouée'
                            : 'insuffisante'}
                      </Badge>
                      <span className="tabular font-mono text-sm text-ink">
                        {int(quality.golden.total)} / {GOLDEN_TARGET} cas
                      </span>
                    </div>
                    <Meter
                      value={quality.golden.total / GOLDEN_TARGET}
                      tone={gateTone(quality.golden.gate)}
                      className="mt-2"
                    />
                    <dl className="mt-4 grid grid-cols-2 gap-3">
                      <div>
                        <dt className="font-mono text-[10px] uppercase tracking-[0.14em] text-ink-faint">
                          Précision (auto)
                        </dt>
                        <dd className="tabular font-mono text-lg text-ink">
                          {quality.golden.precisionAuto === null
                            ? '—'
                            : pct(quality.golden.precisionAuto, 1)}
                        </dd>
                      </div>
                      <div>
                        <dt className="font-mono text-[10px] uppercase tracking-[0.14em] text-ink-faint">
                          Rappel (auto)
                        </dt>
                        <dd className="tabular font-mono text-lg text-ink">
                          {quality.golden.recallAuto === null
                            ? '—'
                            : pct(quality.golden.recallAuto, 1)}
                        </dd>
                      </div>
                    </dl>
                    <p className="mt-2 text-[11px] leading-relaxed text-ink-faint">
                      Seuils de passage : précision ≥ 99 %, rappel ≥ 60 % — appliqués dès 50 cas
                      vérifiés à la main. La porte bloque la diffusion automatique, jamais la revue
                      humaine.
                    </p>
                    {quality.golden.error !== undefined && (
                      <Alert tone="alarm" title="Lecture impossible" className="mt-3">
                        {String(quality.golden.error)}
                      </Alert>
                    )}
                    {quality.golden.failures !== undefined &&
                      quality.golden.failures.length > 0 && (
                        <div className="mt-4">
                          <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-ink-faint">
                            Pires paires ({quality.golden.failures.length})
                          </p>
                          <ul className="mt-2 space-y-1">
                            {quality.golden.failures.slice(0, 8).map((failure) => (
                              <li
                                key={failure.sku}
                                className="flex items-center gap-2 font-mono text-[11px] text-ink-soft"
                              >
                                <span className="w-20 shrink-0 truncate text-ink-soft">
                                  {failure.sku}
                                </span>
                                <span className="text-ink-faint">attendu</span>
                                <span className="text-herb-700">{failure.expected || '∅'}</span>
                                <span className="text-ink-faint">prévu</span>
                                <span className="truncate text-rust-600">
                                  {failure.predicted || '∅'}
                                </span>
                                <span className="tabular ml-auto text-ink-soft">
                                  {score(failure.score)}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                  </>
                )}
              </PanelBody>
            </Panel>
          </div>
        </>
      )}
    </div>
  );
}
